import { hasAnyTranslation } from '@assets/i18n/config';
import { FieldTypes } from '@ovComponents/4-module/configurableOptionFields/fieldTypes';
import type { FieldOption } from '@ovComponents/4-module/configurableOptionFields/schemaSearchField';
import { isValidEmail } from '@utils/common';
import { isNil } from 'lodash-es';

/**
 * Formats a string as a `mailto:` URL if it is a valid email address and doesn't already start with `mailto:`.
 *
 * @param url URL to format
 * @returns {string} formatted URL
 */
export const formatUrl = (url?: string) => {
  if (!url || !isValidEmail(url) || url.startsWith('mailto:')) {
    return url;
  }

  return `mailto:${url}`;
};

/**
 * Get the invalid translated string fields for a widget
 * @param translatedStringOptions - The translated string options
 * @param widgetOptions - The widget options
 * @returns The invalid translated string fields
 */
export const getInvalidTranslatedStringFields = (
  translatedStringOptions: any[],
  widgetOptions: any,
): string[] => {
  return translatedStringOptions
    .filter((elem: any) => {
      if (elem.requiredIf) {
        const [key, value] = Object.entries(elem.requiredIf)[0];
        if (isNil(widgetOptions[key]) || widgetOptions[key] === value) {
          return !hasAnyTranslation(widgetOptions[elem.key]);
        }
        return false;
      } else {
        return !hasAnyTranslation(widgetOptions[elem.key]);
      }
    })
    .map((elem: any) => elem.key);
};

/**
 * Get the invalid schema search fields for a widget
 * @param allOptions - All widget options
 * @param widgetOptions - The widget options values
 * @returns The invalid schema search field keys
 */
export const getInvalidSchemaSearchFieldKeys = (
  allOptions: any[],
  widgetOptions: any,
): string[] => {
  const schemaSearchFieldOptions = allOptions.filter(
    (elem: any) => elem.optionType === FieldTypes.SCHEMA_SEARCH_FIELD,
  );

  return schemaSearchFieldOptions.flatMap((option) => {
    if (!option.key) return [];

    const fieldValues = widgetOptions[option.key] || option.value || [];

    return fieldValues.flatMap((field: FieldOption) => {
      const keys: string[] = [];

      if (!hasAnyTranslation(field.label)) {
        keys.push(`${option.key}-${field.id}-label`);
      }

      if (!field.value?.trim()) {
        keys.push(`${option.key}-${field.id}-value`);
      }

      return keys;
    });
  });
};
